<!-- RegisterModal.vue -->
<script setup>
import { ref, onMounted } from 'vue'
import { useAuthStore } from '../stores/auth'
import { useWebsiteConfigStore } from '../stores/website/configStore'
import LoadingView from '../components/LoadingView.vue'
import { useI18n } from 'vue-i18n'
import LanguageSwitcher from '../components/LanguageSwitcher.vue'

const emit = defineEmits(['register-success', 'close', 'switch-to-login'])

const authStore = useAuthStore()
const websiteStore = useWebsiteConfigStore()
const { t } = useI18n()

// Estados del registro
const name = ref('')
const username = ref('')
const email = ref('')
const password = ref('')
const passwordConfirmation = ref('')
const terms = ref(false)
const adultConfirmation = ref(false)
const affiliateCode = ref('')
const passwordVisible = ref(false)
const passwordConfirmVisible = ref(false)
const loading = ref(false)
const error = ref('')

// Cargar la configuración cuando el componente se monta
onMounted(async () => {
  try {
    if (!websiteStore.config.branding) {
      await websiteStore.loadConfig()
    }
    
    // Buscar el código de afiliado en la URL si existe
    const urlParams = new URLSearchParams(window.location.search)
    const codeParam = urlParams.get('code')
    if (codeParam) {
      affiliateCode.value = codeParam
    }
  } catch (err) {
    console.error('Error loading website config:', err)
  }
})

const togglePasswordVisibility = () => {
  passwordVisible.value = !passwordVisible.value
}

const togglePasswordConfirmVisibility = () => {
  passwordConfirmVisible.value = !passwordConfirmVisible.value
}

const switchToLogin = () => {
  emit('switch-to-login')
}

const handleRegister = async () => {
  try {
    loading.value = true
    error.value = ''

    // Validaciones frontend
    if (!name.value.trim()) {
      error.value = t('auth.errors.emptyName')
      setTimeout(() => { error.value = '' }, 5000)
      return
    }

    if (!username.value.trim()) {
      error.value = t('auth.errors.emptyUsername')
      setTimeout(() => { error.value = '' }, 5000)
      return
    }

    if (!email.value.trim()) {
      error.value = t('auth.errors.emptyEmail')
      setTimeout(() => { error.value = '' }, 5000)
      return
    }

    if (!password.value.trim()) {
      error.value = t('auth.errors.emptyPassword')
      setTimeout(() => { error.value = '' }, 5000)
      return
    }

    if (password.value !== passwordConfirmation.value) {
      error.value = t('auth.errors.passwordMismatch')
      setTimeout(() => { error.value = '' }, 5000)
      return
    }

    if (!terms.value) {
      error.value = t('auth.errors.termsRequired')
      setTimeout(() => { error.value = '' }, 5000)
      return
    }
    
    if (!adultConfirmation.value) {
      error.value = t('auth.errors.adultConfirmationRequired')
      setTimeout(() => { error.value = '' }, 5000)
      return
    }

    // Llamada al registro
    const result = await authStore.register({
      name: name.value,
      username: username.value,
      email: email.value,
      password: password.value,
      password_confirmation: passwordConfirmation.value,
      terms: terms.value,
      adult_confirmation: adultConfirmation.value,
      affiliate_code: affiliateCode.value
    })

    console.log('Resultado del registro:', result)

    // Si el registro fue exitoso
    if (result.success) {
      emit('register-success')
      // Limpiar formulario
      name.value = ''
      username.value = ''
      email.value = ''
      password.value = ''
      passwordConfirmation.value = ''
      terms.value = false
      adultConfirmation.value = false
      affiliateCode.value = ''
    }

  } catch (err) {
    console.log('Error en handleRegister:', err)
    if (err.response && err.response.data && err.response.data.errors) {
      // Mostrar el primer error de validación
      const firstError = Object.values(err.response.data.errors)[0]
      error.value = firstError[0]
    } else {
      error.value = err.message || t('auth.errors.registerFailed')
    }
    setTimeout(() => { error.value = '' }, 5000)
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <LoadingView v-if="loading" />

  <div v-else 
    class="login-background"
    :style="websiteStore.config.branding?.background ? 
      `background-image: url(${websiteStore.config.branding.background})` : ''"
  >
    <div class="content-wrapper">
      <!-- Logo contenedor con posición absoluta para que no afecte al layout del formulario -->
      <div class="logo-container-absolute">
        <img 
          v-if="websiteStore.config.branding?.logo_desktop"
          :src="websiteStore.config.branding.logo_desktop"
          class="logo"
        />
      </div>
      
      <div class="login-form-container">
        <div class="login-form">
          <!-- Nombre completo -->
          <input 
            type="text"
            v-model="name"
            :placeholder="t('auth.namePlaceholder')"
            class="neon-input"
          />
          
          <!-- Nombre de usuario -->
          <input 
            type="text"
            v-model="username"
            :placeholder="t('auth.usernamePlaceholder')"
            class="neon-input"
          />
          
          <!-- Email -->
          <input 
            type="email"
            v-model="email"
            :placeholder="t('auth.emailPlaceholder')"
            class="neon-input"
          />
          
          <!-- Contraseña -->
          <div class="password-container">
            <input 
              :type="passwordVisible ? 'text' : 'password'"
              v-model="password"
              :placeholder="t('auth.passwordPlaceholder')"
              class="neon-input"
            />
            <button 
              type="button"
              @click="togglePasswordVisibility"
              class="toggle-password"
            >
              <font-awesome-icon 
                :icon="passwordVisible ? 'eye-slash' : 'eye'"
                class="password-icon"
              />
            </button>
          </div>
          
          <!-- Confirmar Contraseña -->
          <div class="password-container">
            <input 
              :type="passwordConfirmVisible ? 'text' : 'password'"
              v-model="passwordConfirmation"
              :placeholder="t('auth.confirmPasswordPlaceholder')"
              class="neon-input"
            />
            <button 
              type="button"
              @click="togglePasswordConfirmVisibility"
              class="toggle-password"
            >
              <font-awesome-icon 
                :icon="passwordConfirmVisible ? 'eye-slash' : 'eye'"
                class="password-icon"
              />
            </button>
          </div>
          
          <!-- Código de afiliado -->
          <input 
            type="text"
            v-model="affiliateCode"
            :placeholder="t('auth.affiliateCodePlaceholder')"
            class="neon-input"
          />
          
          <!-- Términos y condiciones con checkbox estilizado -->
          <div class="custom-checkbox-container">
            <label class="custom-checkbox">
              <input 
                type="checkbox"
                v-model="terms"
              />
              <span class="checkmark"></span>
              <span class="checkbox-label">{{ t('auth.acceptTerms') }}</span>
            </label>
          </div>
          
          <!-- Confirmación de mayoría de edad con checkbox estilizado -->
          <div class="custom-checkbox-container">
            <label class="custom-checkbox">
              <input 
                type="checkbox"
                v-model="adultConfirmation"
              />
              <span class="checkmark"></span>
              <span class="checkbox-label">{{ t('auth.adultConfirmation') }}</span>
            </label>
          </div>
          
          <!-- Botón de registro -->
          <button @click="handleRegister" class="neon-button">
            {{ t('auth.register') }}
          </button>
          
          <!-- Enlaces -->
          <div class="links-row">
            <a href="#" @click.prevent="switchToLogin" class="link">{{ t('auth.alreadyHaveAccount') }}</a>
          </div>
          
          <LanguageSwitcher />
          
          <!-- Mensaje de error -->
          <p v-if="error" class="error-message">{{ error }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
@import '../assets/styles/online/OnlineMain.css';
@import '../assets/styles/main.css';

/* Posicionamiento del logo para que no interfiera con el formulario */
.logo-container-absolute {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  z-index: 1;
  padding-top: 20px;

}

.login-form-container {
  z-index: 2;
  position: relative;
  width: 100%;
  padding-top: 80px; /* Espacio para el logo */
}

/* Estilos para el formulario de registro */
.login-form {
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-width: 350px;
  margin: 0 auto;
  padding: 20px;
  background-color: rgba(0, 0, 0, 0.5);
  border-radius: 15px;
  backdrop-filter: blur(10px);
}

/* Checkbox personalizado */
.custom-checkbox-container {
  margin: 10px 0;
}

.custom-checkbox {
  display: flex;
  align-items: center;
  position: relative;
  padding-left: 35px;
  cursor: pointer;
  font-size: 14px;
  color: white;
  user-select: none;
}

.custom-checkbox input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

.checkmark {
  position: absolute;
  top: 0;
  left: 0;
  height: 22px;
  width: 22px;
  background-color: rgba(0, 0, 0, 0.5);
  border: 2px solid #00c2ff;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.custom-checkbox:hover input ~ .checkmark {
  background-color: rgba(0, 194, 255, 0.1);
}

.custom-checkbox input:checked ~ .checkmark {
  background-color: #00c2ff;
}

.checkmark:after {
  content: "";
  position: absolute;
  display: none;
}

.custom-checkbox input:checked ~ .checkmark:after {
  display: block;
}

.custom-checkbox .checkmark:after {
  left: 8px;
  top: 4px;
  width: 5px;
  height: 10px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.checkbox-label {
  margin-left: 5px;
  font-size: 14px;
}

/* Asegurar que el error sea visible */
.error-message {
  color: #ff4263;
  font-size: 14px;
  text-align: center;
  margin-top: 10px;
  padding: 5px;
  background-color: rgba(255, 66, 99, 0.1);
  border-radius: 5px;
}
</style>